import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import type React from 'react'
import { lazy, Suspense } from 'react'
import { Toaster } from 'sonner'
import { ThemeProvider } from '@/hooks/ui'
import type { LandingAppProps } from '@/types/shared'
import { LanguageProvider } from './contexts/LanguageContext'
import LandingLayout from './layouts/LandingLayout'
import MainPage from './pages/MainPage'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      gcTime: 1000 * 60 * 60 * 24 * 7,
      retry: 1,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
    },
  },
})

const componentMap: Record<
  string,
  Record<string, React.LazyExoticComponent<React.FC<LandingAppProps>> | React.FC<LandingAppProps>>
> = {
  landing: {
    index: lazy(() => import('./pages/BlackBook')),
  },
  // `login` is the GET form, `login#login` the re-render after a failed attempt.
  login: {
    new: lazy(() => import('./pages/LoginPage')),
    login: lazy(() => import('./pages/LoginPage')),
  },
  panels: {
    index: lazy(() => import('./pages/PanelsIndex')),
    show: lazy(() => import('./pages/PanelShow')),
  },
  offers: {
    new: lazy(() => import('./pages/OfferNew')),
  },
  pages: {
    home: MainPage, // Eager loaded for instant header animation
    glossary: lazy(() =>
      import('./pages/GlossaryPage').then((module) => ({
        default: module.GlossaryPage,
      })),
    ),
    how_it_works: lazy(() => import('./pages/HowItWorksPage')),
    privacy: lazy(() => import('./pages/PrivacyPage')),
    terms: lazy(() => import('./pages/TermsPage')),
  },
  tools: {
    index: lazy(() => import('./pages/ToolsIndex')),
    combined_wall: lazy(() => import('./pages/CombinedWallTool')),
    section_modulus_calculator: lazy(() => import('./pages/SectionModulusCalculator')),
  },
}

const SheetPileApp: React.FC<LandingAppProps> = ({
  currentPath,
  currentUser,
  csrfToken,
  controller,
  action,
  pageData,
  locale,
  translations,
}) => {
  const Component = componentMap[controller]?.[action]

  return (
    <ThemeProvider defaultTheme="light" storageKey="landing-ui-theme">
      <QueryClientProvider client={queryClient}>
        <LanguageProvider initialLocale={locale} initialTranslations={translations}>
          <LandingLayout
            currentPath={currentPath}
            currentUser={currentUser}
            locale={locale}
            translations={translations}
          >
            <Suspense fallback={<div className="min-h-screen"></div>}>
              {Component ? (
                <Component
                  currentPath={currentPath}
                  currentUser={currentUser}
                  controller={controller}
                  action={action}
                  pageData={pageData}
                  csrfToken={csrfToken}
                  locale={locale}
                  translations={translations}
                />
              ) : (
                <div className="container mx-auto px-4 py-8">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h2 className="text-xl font-semibold mb-2">Page not yet implemented</h2>
                    <p className="text-gray-600">
                      Controller: {controller}, Action: {action}
                    </p>
                  </div>
                </div>
              )}
            </Suspense>
          </LandingLayout>
        </LanguageProvider>
        <Toaster position="top-right" />
      </QueryClientProvider>
    </ThemeProvider>
  )
}

export default SheetPileApp
